import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Message, Checkbox, Loader, SelectPicker, useToaster } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import authFetch from '../../helpers/authFetch';
import { ConditionSelector, FinishSelector } from './CardEntryFormOptions';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const SCRYFALL_ASPECT_RATIO = 204 / 146;
const IMG_WIDTH = 180;
const IMG_HEIGHT = Math.round(IMG_WIDTH * SCRYFALL_ASPECT_RATIO);
const PRINTING_PREVIEW_HEIGHT = 70;
const QTY_DEBOUNCE_MS = 500;
const TOAST_PLACEMENT = 'topCenter';

const TOAST_DURATION = {
  success: 3000,
  error: 4000,
};

const BORDER_RADIUS = {
  image: 8,
  input: 4,
};

const FONT = {
  body: 13,
  label: 12,
};

const SPACING = {
  gap: 16,
  qtyRowGap: 8,
  labelMinWidth: 70,
  qtyDisplayWidth: 20,
  labelMarginBottom: 4,
  rowMarginBottom: 12,
  inputPadding: 6,
  printingPreviewMarginTop: 8,
};

const CardQuickViewModal = ({ open, entry, collectionId, collection, canEdit, onClose, onUpdated, onRemoved }) => {
  const navigate = useNavigate();
  const toaster = useToaster();
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);
  const COLORS = {
    label: t.textFaint,
    muted: t.textMuted,
    inputBorder: t.borderPrimary,
  };

  const deckType = collection?.deck_type;
  const collectionType = collection?.type;

  const [quantity, setQuantity] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [editScryfallId, setEditScryfallId] = useState(null);
  const [editCondition, setEditCondition] = useState('NM');
  const [editFinish, setEditFinish] = useState('nonfoil');
  const [editNotes, setEditNotes] = useState('');
  const [editIsProxy, setEditIsProxy] = useState(false);
  const [editOwned, setEditOwned] = useState(true);
  const [editIsCommander, setEditIsCommander] = useState(false);
  const [editIsSignatureSpell, setEditIsSignatureSpell] = useState(false);
  const [printings, setPrintings] = useState([]);
  const [loadingPrintings, setLoadingPrintings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [settingPreview, setSettingPreview] = useState(false);

  // Debounced quantity save
  const qtyDebounceRef = useRef(null);
  const pendingQtyRef = useRef(null);

  useEffect(() => {
    if (open && entry) {
      setQuantity(entry.quantity || 1);
      setEditScryfallId(entry.scryfall_id);
      setEditCondition(entry.condition || 'NM');
      setEditFinish(entry.finish || 'nonfoil');
      setEditNotes(entry.notes || '');
      setEditIsProxy(entry.is_proxy || false);
      setEditOwned(!entry.is_not_owned);
      setEditIsCommander(entry.is_commander || false);
      setEditIsSignatureSpell(entry.is_signature_spell || false);
      setEditMode(false);
      setConfirmRemove(false);
      setConfirmDiscard(false);
      pendingQtyRef.current = null;

      if (entry.scryfall_id) {
        let mounted = true;
        setLoadingPrintings(true);
        authFetch(`/cards/${entry.scryfall_id}/printings`)
          .then((res) => res.ok ? res.json() : [])
          .then((data) => { if (mounted) setPrintings(data); })
          .catch(() => { if (mounted) setPrintings([]); })
          .finally(() => { if (mounted) setLoadingPrintings(false); });
        return () => { mounted = false; };
      }
    }
  }, [open, entry]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (qtyDebounceRef.current) clearTimeout(qtyDebounceRef.current);
    };
  }, []);

  const saveQuantity = async (newQty) => {
    try {
      const response = await authFetch(`/collections/${collectionId}/entries/${entry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQty }),
      });
      if (response.ok && onUpdated) onUpdated();
      else if (!response.ok) {
        toaster.push(
          <Message type="error" showIcon closable>Failed to update quantity</Message>,
          { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.error }
        );
      }
    } catch {
      toaster.push(
        <Message type="error" showIcon closable>Unable to connect to server</Message>,
        { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.error }
      );
    }
  };

  const flushPendingQty = () => {
    if (qtyDebounceRef.current) {
      clearTimeout(qtyDebounceRef.current);
      qtyDebounceRef.current = null;
    }
    if (pendingQtyRef.current !== null) {
      saveQuantity(pendingQtyRef.current);
      pendingQtyRef.current = null;
    }
  };

  const hasEditChanges = editMode && !!entry && (
    editScryfallId !== entry.scryfall_id ||
    editCondition !== (entry.condition || 'NM') ||
    editFinish !== (entry.finish || 'nonfoil') ||
    editNotes !== (entry.notes || '') ||
    editIsProxy !== (entry.is_proxy || false) ||
    editOwned !== !entry.is_not_owned ||
    editIsCommander !== (entry.is_commander || false) ||
    editIsSignatureSpell !== (entry.is_signature_spell || false)
  );

  const handleClose = () => {
    if (hasEditChanges) {
      setConfirmDiscard(true);
      return;
    }
    flushPendingQty();
    setEditMode(false);
    setConfirmRemove(false);
    setConfirmDiscard(false);
    onClose();
  };

  const handleDiscardConfirm = () => {
    flushPendingQty();
    setEditMode(false);
    setConfirmRemove(false);
    setConfirmDiscard(false);
    onClose();
  };

  const handleQtyChange = (delta) => {
    const newQty = Math.max(1, quantity + delta);
    if (newQty === quantity) return;
    setQuantity(newQty);
    pendingQtyRef.current = newQty;

    if (qtyDebounceRef.current) clearTimeout(qtyDebounceRef.current);
    qtyDebounceRef.current = setTimeout(() => {
      if (pendingQtyRef.current !== null) {
        saveQuantity(pendingQtyRef.current);
        pendingQtyRef.current = null;
      }
      qtyDebounceRef.current = null;
    }, QTY_DEBOUNCE_MS);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const body = {
        condition: editCondition,
        finish: editFinish,
        notes: editNotes.trim() || null,
        is_proxy: editIsProxy,
        is_not_owned: !editOwned,
      };

      if (editScryfallId !== entry.scryfall_id) {
        body.scryfall_id = editScryfallId;
      }

      if (deckType === 'COMMANDER' || deckType === 'OATHBREAKER') {
        body.is_commander = editIsCommander;
      }
      if (deckType === 'OATHBREAKER') {
        body.is_signature_spell = editIsSignatureSpell;
      }

      const response = await authFetch(`/collections/${collectionId}/entries/${entry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        toaster.push(
          <Message type="success" showIcon closable>
            <strong>{entry.name}</strong> updated
          </Message>,
          { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.success }
        );
        setEditMode(false);
        if (onUpdated) onUpdated();
      } else {
        const data = await response.json();
        toaster.push(
          <Message type="error" showIcon closable>
            {data.error || 'Failed to update entry'}
          </Message>,
          { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.error }
        );
      }
    } catch {
      toaster.push(
        <Message type="error" showIcon closable>Unable to connect to server</Message>,
        { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.error }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const response = await authFetch(`/collections/${collectionId}/entries/${entry.id}`, {
        method: 'DELETE',
      });
      if (response.ok || response.status === 204) {
        toaster.push(
          <Message type="success" showIcon closable>
            <strong>{entry.name}</strong> removed from {collectionType === 'DECK' ? 'deck' : 'binder'}
          </Message>,
          { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.success }
        );
        handleClose();
        if (onRemoved) onRemoved();
      } else {
        const data = await response.json();
        toaster.push(
          <Message type="error" showIcon closable>
            {data.error || 'Failed to remove entry'}
          </Message>,
          { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.error }
        );
        setConfirmRemove(false);
      }
    } catch {
      toaster.push(
        <Message type="error" showIcon closable>Unable to connect to server</Message>,
        { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.error }
      );
      setConfirmRemove(false);
    } finally {
      setRemoving(false);
    }
  };

  const handleSetPreviewCard = async () => {
    setSettingPreview(true);
    try {
      const response = await authFetch(`/collections/${collectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview_card_scryfall_id: entry.scryfall_id }),
      });
      if (response.ok) {
        toaster.push(
          <Message type="success" showIcon closable>
            Set <strong>{entry.name}</strong> as deck preview
          </Message>,
          { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.success }
        );
        if (onUpdated) onUpdated();
      } else {
        const data = await response.json();
        toaster.push(
          <Message type="error" showIcon closable>
            {data.error || 'Failed to set preview card'}
          </Message>,
          { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.error }
        );
      }
    } catch {
      toaster.push(
        <Message type="error" showIcon closable>Unable to connect to server</Message>,
        { placement: TOAST_PLACEMENT, duration: TOAST_DURATION.error }
      );
    } finally {
      setSettingPreview(false);
    }
  };

  if (!entry) return null;

  const imageUrl = entry.image_normal || entry.image_small;
  const collectionLabel = collectionType === 'DECK' ? 'deck' : 'binder';
  const finishLabel = { nonfoil: 'Non-Foil', foil: 'Foil', etched: 'Etched' }[entry.finish] ?? entry.finish ?? 'Non-Foil';
  const selectedPrinting = printings.find((p) => p.id === editScryfallId);

  return (
    <Modal open={open} onClose={handleClose} size="sm">
      <Modal.Header>
        <Modal.Title>{entry.name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div style={{ display: 'flex', gap: SPACING.gap }}>
          {imageUrl && (
            <div style={{ flexShrink: 0 }}>
              <img
                src={imageUrl}
                alt={entry.name}
                width={IMG_WIDTH}
                height={IMG_HEIGHT}
                style={{ borderRadius: BORDER_RADIUS.image, display: 'block' }}
              />
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            {entry.set_name && (
              <div style={{ color: COLORS.muted, marginBottom: SPACING.gap, fontSize: FONT.body }}>
                {entry.set_name}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.qtyRowGap, marginBottom: SPACING.rowMarginBottom }}>
              <span style={{ color: COLORS.label, minWidth: SPACING.labelMinWidth, fontSize: FONT.body }}>Quantity</span>
              <Button
                size="xs"
                appearance="ghost"
                onClick={() => handleQtyChange(-1)}
                disabled={!canEdit || quantity <= 1}
              >
                –
              </Button>
              <span style={{ minWidth: SPACING.qtyDisplayWidth, textAlign: 'center' }}>{quantity}</span>
              <Button
                size="xs"
                appearance="ghost"
                onClick={() => handleQtyChange(1)}
                disabled={!canEdit}
              >
                +
              </Button>
            </div>

            {editMode ? (
              <>
                {(loadingPrintings || printings.length > 0) && (
                  <div style={{ marginBottom: SPACING.rowMarginBottom }}>
                    <div style={{ color: COLORS.label, fontSize: FONT.body, marginBottom: SPACING.labelMarginBottom }}>Printing</div>
                    {loadingPrintings ? (
                      <Loader size="xs" content="Loading printings..." />
                    ) : (
                      <>
                        <SelectPicker
                          data={printings.map((p) => ({
                            label: `${p.set_name} (${p.collector_number})`,
                            value: p.id,
                          }))}
                          value={editScryfallId}
                          onChange={setEditScryfallId}
                          searchable={false}
                          cleanable={false}
                          block
                          placeholder="Select printing"
                        />
                        {selectedPrinting?.image_uris?.normal && (
                          <img
                            src={selectedPrinting.image_uris.normal}
                            alt={selectedPrinting.name}
                            style={{ height: PRINTING_PREVIEW_HEIGHT, borderRadius: BORDER_RADIUS.input, marginTop: SPACING.printingPreviewMarginTop }}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}

                <div style={{ marginBottom: SPACING.rowMarginBottom }}>
                  <div style={{ color: COLORS.label, fontSize: FONT.body, marginBottom: SPACING.labelMarginBottom }}>Condition</div>
                  <ConditionSelector value={editCondition} onChange={setEditCondition} />
                </div>
                <div style={{ marginBottom: SPACING.rowMarginBottom }}>
                  <div style={{ color: COLORS.label, fontSize: FONT.body, marginBottom: SPACING.labelMarginBottom }}>Finish</div>
                  <FinishSelector value={editFinish} onChange={setEditFinish} />
                </div>
                <div style={{ marginBottom: SPACING.rowMarginBottom }}>
                  <div style={{ color: COLORS.label, fontSize: FONT.body, marginBottom: SPACING.labelMarginBottom }}>Notes</div>
                  <textarea
                    rows={2}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Optional notes"
                    style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: `1px solid ${COLORS.inputBorder}`, borderRadius: BORDER_RADIUS.input, padding: SPACING.inputPadding, color: 'inherit', resize: 'vertical', fontSize: FONT.body }}
                  />
                </div>

                {collectionType === 'DECK' && (
                  <>
                    <Checkbox
                      checked={editIsProxy}
                      onChange={(_, checked) => {
                        setEditIsProxy(checked);
                        if (checked) setEditOwned(true);
                      }}
                    >
                      Proxy copy
                    </Checkbox>
                    <Checkbox
                      checked={!editOwned}
                      onChange={(_, checked) => {
                        setEditOwned(!checked);
                        if (checked) setEditIsProxy(false);
                      }}
                    >
                      I don't own this card
                    </Checkbox>
                  </>
                )}

                {(deckType === 'COMMANDER' || deckType === 'OATHBREAKER') && (
                  <Checkbox
                    checked={editIsCommander}
                    onChange={(_, checked) => {
                      setEditIsCommander(checked);
                      if (checked && deckType === 'OATHBREAKER') setEditIsSignatureSpell(false);
                    }}
                  >
                    {deckType === 'OATHBREAKER' ? 'Oathbreaker' : 'Commander'}
                  </Checkbox>
                )}

                {deckType === 'OATHBREAKER' && (
                  <Checkbox
                    checked={editIsSignatureSpell}
                    onChange={(_, checked) => {
                      setEditIsSignatureSpell(checked);
                      if (checked) setEditIsCommander(false);
                    }}
                  >
                    Signature Spell
                  </Checkbox>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: SPACING.gap, marginBottom: SPACING.rowMarginBottom }}>
                  <div>
                    <div style={{ color: COLORS.label, fontSize: FONT.label }}>Condition</div>
                    <div>{entry.condition || 'NM'}</div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.label, fontSize: FONT.label }}>Finish</div>
                    <div>{finishLabel}</div>
                  </div>
                </div>
                {entry.notes && (
                  <div style={{ marginBottom: SPACING.rowMarginBottom }}>
                    <div style={{ color: COLORS.label, fontSize: FONT.label }}>Notes</div>
                    <div style={{ color: COLORS.muted, fontSize: FONT.body, wordBreak: 'break-word' }}>{entry.notes}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        {confirmRemove ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.qtyRowGap, justifyContent: 'flex-end' }}>
            <span style={{ color: COLORS.muted, fontSize: FONT.body }}>
              Remove <strong>{entry.name}</strong> from {collectionLabel}?
            </span>
            <Button onClick={() => setConfirmRemove(false)} appearance="subtle" size="sm">
              Cancel
            </Button>
            <Button onClick={handleRemove} appearance="primary" color="red" size="sm" loading={removing}>
              Confirm
            </Button>
          </div>
        ) : confirmDiscard ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.qtyRowGap, justifyContent: 'flex-end' }}>
            <span style={{ color: COLORS.muted, fontSize: FONT.body }}>Discard unsaved changes?</span>
            <Button onClick={() => setConfirmDiscard(false)} appearance="subtle" size="sm">Keep Editing</Button>
            <Button onClick={handleDiscardConfirm} appearance="primary" color="orange" size="sm">Discard</Button>
          </div>
        ) : editMode ? (
          <>
            <Button onClick={handleClose} appearance="subtle" size="sm">Cancel</Button>
            <Button onClick={handleSaveEdit} appearance="primary" size="sm" loading={saving}>Save</Button>
          </>
        ) : (
          <div style={{ display: 'flex', gap: SPACING.qtyRowGap, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              appearance="ghost"
              size="sm"
              onClick={() => { handleClose(); navigate(`/cardsearch/${entry.scryfall_id}`); }}
            >
              View Full Card
            </Button>
            {canEdit && collectionType === 'DECK' && (
              <Button appearance="ghost" size="sm" onClick={handleSetPreviewCard} loading={settingPreview}>
                Set as Preview
              </Button>
            )}
            {canEdit && (
              <>
                <Button appearance="ghost" size="sm" onClick={() => setEditMode(true)}>
                  Edit Entry
                </Button>
                <Button appearance="ghost" color="red" size="sm" onClick={() => setConfirmRemove(true)}>
                  Remove
                </Button>
              </>
            )}
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CardQuickViewModal;
